/**
 * Frontend form field models for schedule-apply.
 *
 * Matches Android: com.indeed.android.ghostwriter.models.ScheduleApplyFormField
 */

import type {
    ScheduleApplyUnboundFormFieldResponse,
    ScheduleApplyOptionsFormFieldResponse,
} from './ScheduleApplyFormFieldResponse';

export enum FieldType {
    Text = 'text',
    Email = 'email',
    Tel = 'tel',
    Url = 'url',
    Textarea = 'textarea',
    Radio = 'radio',
    Checkbox = 'checkbox',
    SingleSelectList = 'single_select_list',
    InputList = 'input_list',
    Date = 'date',
    File = 'file',
    City = 'city',
    State = 'state',
    CityState = 'city_state',
    Country = 'country',
    Select = 'select',
    Information = 'information',
    /** Unbound text with SSN-style validation (see also `validationInputType`). */
    SocialSecurityNumber = 'social_security_number',
    /** Unbound text with postal / ZIP digit validation (see also `validationInputType`). */
    PostalCode = 'postal_code',
}

export type AutocompleteType = 'CITY' | 'STATE' | 'CITY_STATE';

export type ValidationInputType = 'SOCIAL_SECURITY_NUMBER' | 'POSTAL_CODE';

/**
 * Comparison operator for {@link ScheduleApplyFormFieldCondition}. Mirrors
 * the OneGraph `GhostwriterConditionOperator` enum.
 *
 * - `EQ`: show this field only when the parent's current value equals
 *   `condition.value`.
 * - `NE`: show this field only when the parent's current value does *not*
 *   equal `condition.value`.
 */
export type ScheduleApplyFormFieldConditionOperator = 'EQ' | 'NE';

/**
 * Conditional-visibility metadata attached to a form field.
 *
 * When present on a field, the field is only displayed (and only validated /
 * submitted) when the *parent* field — looked up by `fieldName` among the
 * field's siblings in the same group instance — has a current response value
 * that satisfies the comparison.
 *
 * For unbound fields, the parent's current value is its single response
 * string. For options fields (radio / checkbox / select / single-select),
 * the parent is considered to "match" `condition.value` when that value
 * appears in `responseValues` — this generalizes correctly to both single-
 * and multi-select parents.
 */
export interface ScheduleApplyFormFieldCondition {
    /** `fieldName` of the parent field whose value controls visibility. */
    fieldName: string;
    /** The parent's response value to compare against. */
    value: string;
    /** Comparison operator (default `EQ`). */
    operator: ScheduleApplyFormFieldConditionOperator;
}

export interface ScheduleApplyFormFieldBase {
    id: string;
    fieldName: string;
    fieldType: FieldType;
    classification: string;
    autocompleteType?: AutocompleteType | null;
    validationInputType?: ValidationInputType | null;
    label: string;
    description: string | null;
    required: boolean;
    disabled: boolean;
    readonly: boolean;
    /**
     * Conditional visibility metadata. When `null` (or absent), the field is
     * always visible. Mirrors the OneGraph `GhostwriterFormField.condition`
     * field; see {@link ScheduleApplyFormFieldCondition} for semantics.
     */
    condition?: ScheduleApplyFormFieldCondition | null;
    /**
     * `fieldName` of the sibling form field that this field re-asks the user
     * to confirm (e.g. for a "Re-enter email" pair, the confirm field carries
     * `confirmsFieldName === <primary email's fieldName>`). When `null` (or
     * absent), the field is not a confirmation field.
     *
     * Mirrors the OneGraph `GhostwriterFormField.confirmsFieldName` field.
     */
    confirmsFieldName?: string | null;
}

export interface ScheduleApplyUnboundFormField extends ScheduleApplyFormFieldBase {
    placeholder: string;
    response: ScheduleApplyUnboundFormFieldResponse;
}

export interface ScheduleApplyOptionsFormField extends ScheduleApplyFormFieldBase {
    options: ScheduleApplyFormFieldOption[];
    response: ScheduleApplyOptionsFormFieldResponse;
}

export interface ScheduleApplyFormFieldOption {
    value: string;
    label: string;
}

export interface ScheduleApplyFormFieldGroup {
    groupLabel: string;
    formFields: ScheduleApplyFormFieldBase[];
    groupId: string;
    isCollapsed: boolean;
    nestedFormFieldGroup: ScheduleApplyFormFieldGroup | null;
    /**
     * Mirrors `GhostwriterResponseGroup.canCreateNewResponseGroup` — when
     * `true`, the user may add additional response entries to this group
     * (e.g. multiple Education History rows). The renderer surfaces an
     * "Add another {groupLabel}" affordance.
     *
     * On the wire, additional entries are submitted as separate
     * `GhostwriterResponseGroupInput`s with the *same* `fieldGroupId` and
     * sequential `displayOrder`s; client-side, each entry has its own
     * stable instance ID with draft keys formatted by
     * {@link buildInstancedFieldId}.
     *
     * The flag is sourced from the application's response groups (the
     * field-field-group definition does not carry it directly): all
     * response groups belonging to a given form field group share this
     * flag, and groups with no matching response group default to `false`.
     */
    canCreateNewResponseGroup: boolean;
}

/**
 * Returns the draft-state key for a field, given an optional instance suffix.
 *
 * - When `suffix` is `null` or `undefined`, returns the bare `fieldId`. This is
 *   used for non-repeatable groups and for the *primary* (first) instance of
 *   repeatable groups, preserving backward compatibility with the existing
 *   draft map keying.
 * - Otherwise returns `${fieldId}#${suffix}`. Used for additional instances of
 *   repeatable groups.
 *
 * The `#` separator is intentionally absent from any backend-issued field ID
 * (which are GraphQL IDs / UUIDs) so collisions are impossible.
 */
export function buildInstancedFieldId(
    fieldId: string,
    suffix: string | null | undefined,
): string {
    return suffix == null ? fieldId : `${fieldId}#${suffix}`;
}
